import { useRef, useEffect, RefObject, useCallback } from "react";

/**
 * Generic hook that automatically scrolls a container to the bottom when items are added to an array.
 *
 * @param items - Array of items to monitor for length changes
 * @param scrollContainerRef - Ref to the scrollable container element
 * @returns Object containing lastItemRef and scrollToBottom function
 */
const useAutoScroll = <T>(items: T[], scrollContainerRef: RefObject<HTMLDivElement>) => {
  const lastItemRef = useRef<HTMLDivElement>(null);
  const prevItemsLength = useRef(items.length);

  const scrollToBottom = useCallback(() => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const targetScrollTop = container.scrollHeight - container.clientHeight;
      const startTime = performance.now();
      const duration = 300; // Animation duration in milliseconds

      const animateScroll = (currentTime: number) => {
        const elapsedTime = currentTime - startTime;
        const progress = Math.min(elapsedTime / duration, 1);
        const easeProgress = 1 - Math.pow(1 - progress, 3); // Cubic ease-out

        container.scrollTop =
          container.scrollTop + (targetScrollTop - container.scrollTop) * easeProgress;

        if (progress < 1) {
          requestAnimationFrame(animateScroll);
        }
      };

      requestAnimationFrame(animateScroll);
    }
  }, [scrollContainerRef]);

  useEffect(() => {
    if (items.length > prevItemsLength.current) {
      setTimeout(scrollToBottom, 100);
    }
    prevItemsLength.current = items.length;
  }, [items.length, scrollToBottom]);

  return { scrollContainerRef, lastItemRef, scrollToBottom };
};

export default useAutoScroll;
